import React, { useState } from 'react';
import type { ReactNode } from 'react';
import { ArrowLeftIcon, CheckIcon, ChevronDownIcon, SaveIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { TextField } from '../components/ui/TextField';
import { Toggle } from '../components/ui/Toggle';
import { departmentNames } from '../data/org';
import { useHRStore } from '../hooks/useHRStore';
import type { AccrualFrequency, AnniversaryMode, Department, Navigate, PolicyForm, PolicyType } from '../types/hr';
import { hrActions } from '../utils/hrStore';
import { buildPolicy, describePolicy } from '../utils/policy';
const BLANK: PolicyForm = {
  name: '',
  type: 'Accrual',
  accrualRate: '',
  accrualFrequency: 'Monthly',
  waitingDays: '0',
  prorate: true,
  yearOneCap: '',
  anniversaryMode: 'top-up',
  anniversaryAmount: '',
  carryover: '',
  departments: []
};
const TYPES: {
  id: PolicyType;
  hint: string;
}[] = [{
  id: 'Accrual',
  hint: 'Earn over time'
}, {
  id: 'Fixed grant',
  hint: 'Lump sum each year'
}, {
  id: 'Unlimited',
  hint: 'Tracked, no balance'
}];
const FREQUENCIES: AccrualFrequency[] = ['Monthly', 'Per pay period', 'Annually'];
const ANNIVERSARY_MODES: {
  id: AnniversaryMode;
  title: string;
  hint: string;
}[] = [{
  id: 'top-up',
  title: 'Top up to X days',
  hint: 'Bring balance up to a target'
}, {
  id: 'add',
  title: 'Add X days',
  hint: 'Grant a flat amount on top'
}];
export function PolicyEditor({
  policyId,
  onNavigate
}: {
  policyId?: string;
  onNavigate: Navigate;
}) {
  const {
    employees,
    policies
  } = useHRStore();
  const existing = policies.find(p => p.id === policyId);
  const [form, setForm] = useState<PolicyForm>(() => {
    const source = existing ? existing.form : BLANK;
    return {
      ...BLANK,
      ...source,
      departments: [...(source.departments ?? [])]
    };
  });
  const [submitted, setSubmitted] = useState(false);
  function set<K extends keyof PolicyForm>(key: K, value: PolicyForm[K]) {
    setForm(f => ({
      ...f,
      [key]: value
    }));
  }
  const nameError = submitted && !form.name.trim() ? 'Give the policy a name' : undefined;
  const matched = employees.filter(e => form.departments.includes(e.department) && e.status !== 'Terminated').length;
  const amount = Number(form.anniversaryAmount) || 0;
  const preview = describePolicy(form);
  const cancel = () => onNavigate(existing ? {
    page: 'policy',
    policyId: existing.id
  } : {
    page: 'timeoff',
    tab: 'policies'
  });
  const save = () => {
    setSubmitted(true);
    if (!form.name.trim()) {
      toast.error('Please fix the highlighted fields');
      return;
    }
    const id = existing ? existing.id : `policy-${Date.now()}`;
    hrActions.savePolicy(buildPolicy(existing, form, id));
    toast.success(existing ? 'Policy updated' : 'Policy created', {
      description: `${form.name.trim()} saved — the summary and policy card now reflect these rules.`
    });
    onNavigate({
      page: 'policy',
      policyId: id
    });
  };
  const toggleDept = (d: Department) => set('departments', form.departments.includes(d) ? form.departments.filter(x => x !== d) : [...form.departments, d]);
  return <div className="pb-24" data-id="element-986" data-mp="3bsv1ta67gu5i">
      <button type="button" onClick={cancel} className="mb-4 inline-flex items-center gap-1.5 text-xs font-medium text-muted transition hover:text-navy" data-id="element-987" data-mp="3bu6g43xa1kav">
        <ArrowLeftIcon className="h-3.5 w-3.5" data-id="element-988" data-mp="370liiczlisc7" /> {existing ? existing.name : 'Policies'}
      </button>
      <div className="mb-8" data-id="element-989" data-mp="3gs7zyr2j1nne">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-azure" data-id="element-990" data-mp="3gt0xr2jid2te">{existing ? 'Edit policy' : 'New policy'}</p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-navy md:text-[28px]" data-id="element-991" data-mp="3urh3aq5mwn9y">{existing ? `Edit ${existing.name}` : 'Create a time off policy'}</h1>
        <p className="mt-1.5 text-sm text-muted" data-id="element-992" data-mp="20dg609pqg8bz">Leave a value blank to keep it marked as TBD on the policy summary.</p>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_320px]" data-id="element-993" data-mp="1vg6g6syflunm">
        <div className="space-y-6" data-id="element-994" data-mp="1xsi6gr7iuk55">
          <Section step={1} title="Basics" data-id="element-995" data-mp="1xvmho8jemg5b">
            <TextField label="Policy name" value={form.name} onChange={v => set('name', v)} placeholder="e.g. Regular PTO" error={nameError} data-id="element-996" data-mp="21ulsg9rth74o" />
            <div className="mt-5" data-id="element-997" data-mp="26v15f086scm4">
              <p className="text-xs font-medium text-slate-600" data-id="element-998" data-mp="2bt2ovm9iydy4">Policy type</p>
              <div className="mt-1.5 grid grid-cols-1 gap-2 sm:grid-cols-3" data-id="element-999" data-mp="2bqynbaz3lr10">
                {TYPES.map(t => <ChoiceCard key={t.id} selected={form.type === t.id} onClick={() => set('type', t.id)} title={t.id} hint={t.hint} data-id="element-1000" data-mp="1j4ndratikfs2" />)}
              </div>
            </div>
          </Section>

          <Section step={2} title="Accrual & eligibility" data-id="element-1001" data-mp="1su6xsjsyqfoi">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-3" data-id="element-1002" data-mp="1svrrk77gdulb">
              <TextField label="Accrual rate" value={form.accrualRate} onChange={v => set('accrualRate', v)} suffix="days" inputMode="decimal" placeholder="TBD" data-id="element-1003" data-mp="ve1ikk4lypzh" />
              <div data-id="element-1004" data-mp="sud6kwvmirl1">
                <label htmlFor="frequency" className="block text-xs font-medium text-slate-600" data-id="element-1005" data-mp="suwqgj4u3q1p">
                  Frequency
                </label>
                <div className="relative mt-1.5" data-id="element-1006" data-mp="nth1ifzjcze1">
                  <select id="frequency" value={form.accrualFrequency} onChange={e => set('accrualFrequency', e.target.value as AccrualFrequency)} className="h-11 w-full appearance-none rounded-xl border-0 bg-white px-3.5 text-sm text-navy ring-1 ring-inset ring-slate-200 focus:outline-none focus:ring-2 focus:ring-azure" data-id="element-1007" data-mp="ivnxntlpghgx">
                    {FREQUENCIES.map(f => <option key={f} data-id="element-1008" data-mp="3rciq0f4vmjgr">{f}</option>)}
                  </select>
                  <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" data-id="element-1009" data-mp="3mcxru6xj6f53" />
                </div>
              </div>
              <TextField label="Waiting period" value={form.waitingDays} onChange={v => set('waitingDays', v)} suffix="days" inputMode="numeric" data-id="element-1010" data-mp="3idoxp0ykwf90" />
            </div>
            <div className="mt-5 flex items-center justify-between rounded-xl bg-canvas px-4 py-3" data-id="element-1011" data-mp="3cqmu55eaefoa">
              <div data-id="element-1012" data-mp="2yr3vr4ba2ip6">
                <p className="text-sm font-medium text-navy" data-id="element-1013" data-mp="2yrn35r3axooy">Prorate first period</p>
                <p className="text-xs text-muted" data-id="element-1014" data-mp="33pookio7a03l">Partial months accrue by days employed</p>
              </div>
              <Toggle checked={form.prorate} onChange={v => set('prorate', v)} label="Prorate first period" srOnlyLabel data-id="element-1015" data-mp="3rf9h9ta5obq2" />
            </div>
          </Section>

          <Section step={3} title="Caps & carryover" data-id="element-1016" data-mp="3hpg6uvlr22ne">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2" data-id="element-1017" data-mp="3hmk5exap0vu7">
              <TextField label="Year-one cap" value={form.yearOneCap} onChange={v => set('yearOneCap', v)} suffix="days" placeholder="TBD" hint="Accrual pauses at this balance" data-id="element-1018" data-mp="3mmga6yr7srat" />
              <TextField label="Carryover limit" value={form.carryover} onChange={v => set('carryover', v)} suffix="days" placeholder="TBD" hint="Max balance carried into next year" data-id="element-1019" data-mp="izprx5clvdga" />
            </div>
          </Section>

          <Section step={4} title="Anniversary rule" data-id="element-1020" data-mp="obd135b23c5d">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-id="element-1021" data-mp="123sx3m5ji9ws">
              {ANNIVERSARY_MODES.map(m => <ChoiceCard key={m.id} selected={form.anniversaryMode === m.id} onClick={() => set('anniversaryMode', m.id)} title={m.title} hint={m.hint} data-id="element-1022" data-mp="x3wvftkbeuke" />)}
            </div>
            <div className="mt-5 grid grid-cols-1 items-end gap-5 sm:grid-cols-2" data-id="element-1023" data-mp="5dsc7y8akdkr">
              <TextField label="X (days)" value={form.anniversaryAmount} onChange={v => set('anniversaryAmount', v)} suffix="days" placeholder="TBD" data-id="element-1024" data-mp="jcib9jeo8nbr" />
              <p className="rounded-xl bg-flame/[0.06] px-4 py-3 text-xs leading-relaxed text-slate-600 ring-1 ring-inset ring-flame/15" data-id="element-1025" data-mp="ec42u095rbd7">
                {amount > 0 ? form.anniversaryMode === 'top-up' ? <>
                      Example: 10 days banked → <span className="font-semibold text-navy" data-id="element-1026" data-mp="3kadj1vynarqn">+{Math.max(0, amount - 10)} days</span> → {Math.max(10, amount)}
                    </> : <>
                      Example: 10 days banked → <span className="font-semibold text-navy" data-id="element-1027" data-mp="3t8foqotbq8k9">+{amount} days</span> → {10 + amount}
                    </> : 'Enter X to preview how the rule applies.'}
              </p>
            </div>
          </Section>

          <Section step={5} title="Assign employees" data-id="element-1028" data-mp="brfsi3463qb0">
            <p className="text-xs text-muted" data-id="element-1029" data-mp="fum6jwxiuls1">Choose departments to enroll. Individuals can be added from their profile.</p>
            <div className="mt-3 flex flex-wrap gap-2" data-id="element-1030" data-mp="ksnqsl6oor53">
              {departmentNames.map(d => {
              const on = form.departments.includes(d);
              return <button key={d} type="button" aria-pressed={on} onClick={() => toggleDept(d)} className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium ring-1 ring-inset transition ${on ? 'bg-navy text-white ring-navy' : 'bg-white text-slate-600 ring-slate-200 hover:ring-slate-300'}`} data-id="element-1031" data-mp="2mmd7t6rqaf6a">
                    {on && <CheckIcon className="h-3.5 w-3.5" data-id="element-1032" data-mp="2y0wy86yjuaae" />}
                    {d}
                  </button>;
            })}
            </div>
            <p className="mt-4 text-sm text-slate-600" data-id="element-1033" data-mp="2szr90j43l55m">
              <span className="font-semibold text-navy" data-id="element-1034" data-mp="2o164i7htbzbd">{matched}</span> sample employees work in the selected departments.
            </p>
          </Section>
        </div>

        <aside className="space-y-6 xl:sticky xl:top-24 xl:self-start" data-id="element-1035" data-mp="1cntzhtabhanm">
          <section className="rounded-2xl bg-navy p-6 text-white shadow-lift" aria-live="polite" data-id="element-1036" data-mp="18lqkiuofvnxl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-azure" data-id="element-1037" data-mp="2ivvyod3uif0g">Rule preview</p>
            <p className="mt-3 text-[15px] leading-relaxed" data-id="element-1038" data-mp="2eukiey6v364x">{preview.sentence}</p>
            {preview.tbd.length > 0 ? <div className="mt-4 flex flex-wrap gap-1.5" data-id="element-1039" data-mp="2slz0n2sy33ax">
                {preview.tbd.map(t => <span key={t} className="rounded-full bg-amber-400/15 px-2.5 py-0.5 text-[11px] font-medium text-amber-300 ring-1 ring-inset ring-amber-400/30" data-id="element-1040" data-mp="2ntxu7xigk6st">
                    {t}: TBD
                  </span>)}
              </div> : <p className="mt-4 inline-flex items-center gap-1.5 text-xs text-emerald-300" data-id="element-1041" data-mp="3bkbakqrokh7k">
                <CheckIcon className="h-3.5 w-3.5" data-id="element-1042" data-mp="36lgsxs5g8yp1" /> All rules defined
              </p>}
          </section>
          <Card title="Live summary" data-id="element-1043" data-mp="1h5qvctm4pdvk">
            <ul className="space-y-3 text-sm" data-id="element-1044" data-mp="1h6sjnzs5nwv8">
              <SummaryRow label="Name" value={form.name} data-id="element-1045" data-mp="1c7pfsinwvwou" />
              <SummaryRow label="Type" value={form.type} data-id="element-1046" data-mp="1c9aaq9e51hn8" />
              <SummaryRow label="Accrual" value={form.accrualRate ? `${form.accrualRate} days · ${form.accrualFrequency.toLowerCase()}` : ''} data-id="element-1047" data-mp="3nuj6w2fokc8u" />
              <SummaryRow label="Waiting period" value={form.waitingDays ? `${form.waitingDays} days` : ''} data-id="element-1048" data-mp="3sw02bifeb6i1" />
              <SummaryRow label="Proration" value={form.prorate ? 'On' : 'Off'} data-id="element-1049" data-mp="3f0o8dd92dk26" />
              <SummaryRow label="Year-one cap" value={form.yearOneCap ? `${form.yearOneCap} days` : ''} data-id="element-1050" data-mp="3j29fehyx344g" />
              <SummaryRow label="Anniversary" value={amount ? form.anniversaryMode === 'top-up' ? `Top up to ${amount}` : `Add ${amount}` : ''} data-id="element-1051" data-mp="356e7t89yxfgd" />
              <SummaryRow label="Carryover" value={form.carryover ? `${form.carryover} days` : ''} data-id="element-1052" data-mp="2w4ep8bjb23bb" />
              <SummaryRow label="Departments" value={form.departments.length ? `${form.departments.length} selected` : ''} data-id="element-1053" data-mp="304gh5sz8nide" />
            </ul>
          </Card>
        </aside>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-slate-200 bg-white/90 backdrop-blur-md lg:left-72" data-id="element-1054" data-mp="yedd20vicwro">
        <div className="flex items-center justify-end gap-3 px-6 py-4 lg:px-10" data-id="element-1055" data-mp="27bv0o10eewf0">
          <span className="mr-auto hidden text-xs text-muted sm:block" data-id="element-1056" data-mp="22b7bzlzn6rny">Unsaved changes are discarded on cancel.</span>
          <Button onClick={cancel} data-id="element-1057" data-mp="1yb5773ym1i4g">Cancel</Button>
          <Button variant="primary" icon={SaveIcon} onClick={save} data-id="element-1058" data-mp="1ybyhgews2prc">
            Save policy
          </Button>
        </div>
      </div>
    </div>;
}
function Section({
  step,
  title,
  children
}: {
  step: number;
  title: string;
  children: ReactNode;
}) {
  return <section className="rounded-2xl bg-white p-6 shadow-soft ring-1 ring-slate-900/[0.04]" data-id="element-1059" data-mp="2m080j92lwzes">
      <div className="mb-5 flex items-center gap-3" data-id="element-1060" data-mp="2h7ols8s6ny8e">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-navy text-xs font-semibold text-white" data-id="element-1061" data-mp="2c9wnmx0y3fvr">{step}</span>
        <h2 className="text-[15px] font-semibold text-navy" data-id="element-1062" data-mp="39qc27aw8ip35">{title}</h2>
      </div>
      {children}
    </section>;
}
function ChoiceCard({
  selected,
  onClick,
  title,
  hint
}: {
  selected: boolean;
  onClick: () => void;
  title: string;
  hint: string;
}) {
  return <button type="button" onClick={onClick} aria-pressed={selected} className={`flex items-start gap-3 rounded-xl px-4 py-3 text-left ring-1 ring-inset transition ${selected ? 'bg-azure/[0.06] ring-2 ring-azure' : 'bg-white ring-slate-200 hover:ring-slate-300'}`} data-id="element-1063" data-mp="3h8rzroqxd837">
      <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full ring-1 ring-inset ${selected ? 'bg-azure ring-azure' : 'ring-slate-300'}`} data-id="element-1064" data-mp="3v8ko5dezbc80">
        {selected && <span className="h-1.5 w-1.5 rounded-full bg-white" data-id="element-1065" data-mp="406xuzgn4md0" />}
      </span>
      <span data-id="element-1066" data-mp="8ti3wydmoz3v">
        <span className="block text-sm font-medium text-navy" data-id="element-1067" data-mp="8v300sflcq3u">{title}</span>
        <span className="block text-xs text-muted" data-id="element-1068" data-mp="dqrtce10parp">{hint}</span>
      </span>
    </button>;
}
function SummaryRow({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <li className="flex items-center justify-between gap-3" data-id="element-1069" data-mp="mrzlsfgurhdv">
      <span className="text-muted" data-id="element-1070" data-mp="960y4j5repod">{label}</span>
      {value ? <span className="truncate text-right font-medium text-navy" data-id="element-1071" data-mp="947p4x7obtd7">{value}</span> : <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-500/30" data-id="element-1072" data-mp="53xhlg48guw8">TBD</span>}
    </li>;
}